import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { KeyRound, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";

const LOGO_IMG = "/img/n148kmw9_image.webp";

const SetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!token) {
        setStatus("invalid");
        return;
      }
      try {
        const data = await apiFetch("/auth/check-token", { method: "POST", body: { token } });
        setInfo(data);
        setStatus("valid");
      } catch (e) {
        setStatus("invalid");
      }
    };
    check();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError("Heslo musí mít alespoň 6 znaků");
      return;
    }
    if (password !== confirm) {
      setError("Hesla se neshodují");
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiFetch("/auth/set-password", { method: "POST", body: { token, password } });
      setSession(data.token, data.user);
      navigate(data.user?.role === "partner" ? "/flotila" : data.user?.role === "worker" ? "/prace" : (data.user?.role === "admin" || data.user?.role === "assistant") ? "/admin" : "/klient", { replace: true });
    } catch (err) {
      setError(err.message || "Nepodařilo se nastavit heslo");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="set-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO_IMG} alt="EffectDeal" className="w-10 h-10 object-contain" />
          <span className="font-['Cabinet_Grotesk'] font-bold text-xl tracking-wide">EFFECTDEAL</span>
        </Link>

        <div className="glass p-6 sm:p-8 rounded-2xl">
          {status === "checking" && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto" />
            </div>
          )}

          {status === "invalid" && (
            <div className="text-center py-4" data-testid="set-password-invalid">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <h1 className="font-['Cabinet_Grotesk'] text-xl font-bold mb-2">Neplatný odkaz</h1>
              <p className="text-zinc-400 text-sm mb-6">
                Odkaz pro nastavení hesla je neplatný nebo jeho platnost vypršela. Kontaktujte nás prosím.
              </p>
              <Link to="/prihlaseni" className="btn-accent inline-block">Přejít na přihlášení</Link>
            </div>
          )}

          {status === "valid" && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-indigo-400" />
                </div>
                <h1 className="font-['Cabinet_Grotesk'] text-xl sm:text-2xl font-bold">Nastavení hesla</h1>
              </div>
              <p className="text-zinc-400 text-sm mb-6">
                Vítejte{info?.name ? `, ${info.name}` : ""}. Nastavte si heslo pro účet{" "}
                <span className="text-indigo-400">{info?.email}</span>.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="set-password-form">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Nové heslo</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-dark text-base"
                    data-testid="set-password-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Heslo znovu</label>
                  <input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="input-dark text-base"
                    data-testid="set-password-confirm"
                  />
                </div>

                {error && <p className="text-red-400 text-sm" data-testid="set-password-error">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="set-password-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Nastavit heslo a přihlásit
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
